import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { capitalizeString } from 'shared-helpers/common';
import { useApiData } from 'lib/api';
import { wikiLangCodes } from 'lib/lang';
import { FlagIcon, ModalButtonWithForm, MoreOptions } from 'components/common';
import { Box } from 'components/common/box';
import { Form, FormGroup, Select } from 'components/common/form';
import { SettingsExportLanguageVersions, SettingsLanguageVersions } from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';

const SettingsLanguage = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: { defaultSystemLanguage }
  } = useReduxState();

  const {
    data: translationProgressData,
    loading,
    updateData: updateTranslationProgress
  } = useApiData<GetCourseTranslationProgressRes>({
    url: `/admin/courses/${course.id}/translation-progress`
  });

  const handleLanguageSettingsSubmit = async (values: typeof initialValues) => {
    try {
      const data = {
        ...values,
        language: values.language || defaultSystemLanguage
      };

      await api.put(`/admin/courses/${course.id}`, data);
      const courses = await api.get(`/admin/courses?id=${course.id}`);

      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });

      // refresh translation progress data after updating language
      await updateTranslationProgress();

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues = {
    language: course.language || defaultSystemLanguage
  };

  return (
    <div className="language">
      <Form allowEmptyStrings={true} initialValues={initialValues}>
        {() => (
          <>
            <Box title={messages.courseLanguageVersionsDefault}>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 20,
                  width: '100%'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FlagIcon
                    lang={course.language}
                    style={{ marginRight: 6, marginTop: 1 }}
                  />

                  <span style={{ fontSize: '1.6rem', marginRight: 6 }}>
                    {capitalizeString(
                      wikiLangCodes.find((l) => l.value === course.language)
                        ?.label || course.language
                    )}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ModalButtonWithForm
                    buttonCategory="lighter"
                    buttonIcon="pen"
                    buttonSize="header"
                    buttonText={messages.courseLanguageVersionsChange}
                    initialValues={initialValues}
                    modalActionCategory="success"
                    modalActionText={messages.commonSaveChanges}
                    modalTitle={messages.courseLanguageVersionsDefault}
                    modalWidth="medium"
                    onSubmit={handleLanguageSettingsSubmit}
                  >
                    {() => (
                      <>
                        <FormGroup
                          htmlFor="language"
                          label={messages.courseLanguageVersionsDefault}
                          labelHelpText={messages.courseLanguageHelpText}
                        >
                          <Select
                            field="language"
                            id="language"
                            isSearchable={true}
                            options={wikiLangCodes
                              .slice()
                              // sort alphabetically
                              .sort((a, b) => a.label.localeCompare(b.label))
                              // filter out existing language versions
                              .filter(
                                (option) =>
                                  !course.languageVersions ||
                                  !course.languageVersions[option.value]
                              )
                              .map((option) => ({
                                label: capitalizeString(option.label),
                                value: option.value
                              }))}
                            style={{ maxWidth: 260 }}
                          />
                        </FormGroup>
                      </>
                    )}
                  </ModalButtonWithForm>

                  {course.languageVersions &&
                    Object.keys(course.languageVersions).length > 0 && (
                      <MoreOptions
                        category="lighter"
                        styleContentButtons={false}
                      >
                        <SettingsExportLanguageVersions
                          buttonIsDropdown={true}
                          buttonTooltip={
                            messages.courseLanguageVersionsExportDefaultLanguageTooltip
                          }
                          targetLanguage={course.language}
                        />
                      </MoreOptions>
                    )}
                </div>
              </div>
            </Box>
          </>
        )}
      </Form>

      <SettingsLanguageVersions
        loading={loading}
        translationProgressData={translationProgressData?.progressByLanguage}
        updateTranslationProgress={updateTranslationProgress}
      />

      <style jsx>{`
        .lang-flag-label {
          display: flex;
          align-items: center;
        }
        .lang-label {
          display: inline;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .lang-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default SettingsLanguage;
